import { styled } from '@abyss/web/tools/styled';
import { Box } from '@abyss/web/ui/Box';
import { Card } from '@abyss/web/ui/Card';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { Text } from '@abyss/web/ui/Text';
import { useRouter } from 'next/compat/router';

import { Button } from '@pass-frontend/abyss/common/components/button/ui';
import { ContentWidthWrapper } from '@pass-frontend/abyss/common/components/content-width-wrapper/ui';
import { MEDIA_QUERY } from '@pass-frontend/abyss/utility/style-helpers';
import { getRegistrationUrl } from '@pass-frontend/common/constants/paths';
import { useIntl } from '@pass-frontend/common/i18n';
import { useStandaloneBreakpoints } from '@pass-frontend/features/standalone/data-bridge';

const CONTENT_WRAPPER_STYLES = {
  'abyss-flex-root': {
    margin: '$core-spacing-800 0 72px',
    [MEDIA_QUERY.mobileAndTablet]: {
      margin: '$lg 19px $core-spacing-400',
    },
    '& .content-width-wrapper__limiter': {
      padding: '0',
      maxWidth: '1280px',
    },
  },
};

const CUSTOM_BOX_STYLES = {
  'abyss-box-root': {
    borderRadius: '40px',
    [MEDIA_QUERY.mobileAndTablet]: {
      borderRadius: '$core-borderRadius-xl',
    },
  },
};

const STEP_CONTAINER_STYLES = {
  'abyss-flex-root': {
    margin: '0',
    height: 'auto',
    [MEDIA_QUERY.mobileAndTablet]: {
      margin: '0 $md',
      height: '352px',
    },
  },
};

const STEP_DESCRIPTION_TEXT_STYLES = {
  'abyss-text-root': {
    textAlign: 'center' as const,
    margin: '$md 0 $core-spacing-150',
    lineHeight: '$web-core-lineHeight-140',
    [MEDIA_QUERY.mobileAndTablet]: {
      textAlign: 'left' as const,
      margin: '$sm 0 0',
    },
  },
};

const TIER_CARD_CONTAINER_STYLES = {
  'abyss-flex-root': {
    marginTop: '$core-spacing-400',
    [MEDIA_QUERY.mobileAndTablet]: {
      margin: '$core-spacing-600 21px',
    },
  },
};

const CARD_HEADER_SECTION_STYLES = { 'abyss-card-section': { padding: '0' } };

const TIER_CARD_HEADER_TEXT_STYLES = {
  'abyss-text-root': { lineHeight: '28px' },
};

const CUSTOM_CARD_STYLES = {
  'abyss-card-root': {
    border: '$core-borderWidth-sm solid $tint4',
    borderRadius: '$core-borderRadius-md',
    boxShadow: 'none',
    overflow: 'hidden',
    marginTop: '0 !important',
    [MEDIA_QUERY.mobileAndTablet]: {
      width: '100%',
      height: '100%',
      marginTop: '$md !important',
    },
  },
};

const CUSTOM_CARD_SECTION_STYLES = {
  'abyss-card-section': {
    padding: '$md',
    borderTop: '0 !important',
    [MEDIA_QUERY.mobileAndTablet]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
};

const ADDITIONAL_TIER_INFO_STYLES = {
  'abyss-text-root': {
    lineHeight: '$web-core-lineHeight-140',
  },
};

const PRICING_INFO_CONTAINER_STYLES = {
  'abyss-flex-root': {
    marginTop: '$lg',
    [MEDIA_QUERY.mobileAndTablet]: {
      marginTop: '$md',
    },
  },
};

const PER_MONTH_TEXT_STYLES = {
  'abyss-text-root': {
    marginBottom: '$core-spacing-150',
    lineHeight: '14px',
    [MEDIA_QUERY.mobileAndTablet]: {
      marginBottom: '$sm',
      lineHeight: '$web-core-lineHeight-120',
    },
  },
};

const SAVING_INFO_TEXT_STYLES = {
  'abyss-text-root': {
    lineHeight: '$core-lineHeight-sm',
    [MEDIA_QUERY.mobileAndTablet]: {
      lineHeight: '$web-core-lineHeight-140',
    },
  },
};

const FIND_YOUR_FIT_CONTAINER_STYLES = {
  'abyss-flex-root': {
    margin: '$core-spacing-700 0',
    [MEDIA_QUERY.mobileAndTablet]: {
      margin: '0 $md $core-spacing-400',
    },
  },
};

const PRICING_INFO_TEXT_STYLES = {
  'abyss-text-root': {
    lineHeight: '$web-core-lineHeight-140',
    marginTop: '$md',
    textAlign: 'center' as const,
    padding: '0',
    [MEDIA_QUERY.mobileAndTablet]: {
      padding: '0 26px',
    },
  },
};

const Container = styled(Flex, {
  width: 'stretch',
  maxWidth: '1054px',
  [MEDIA_QUERY.desktop]: {
    margin: '0 $core-spacing-200',
  },
});

const Step = styled(Flex, {
  maxWidth: '319.3px',
  [MEDIA_QUERY.mobileAndTablet]: {
    maxWidth: '100%',
    flexWrap: 'nowrap',
  },
});

const StyledHeading = styled(Heading, {
  margin: '$core-spacing-700 0',
  padding: '10px 0',
  fontSize: '$web-core-fontSize-h-desktop-80 !important',
  lineHeight: '100% !important',
  [MEDIA_QUERY.mobileAndTablet]: {
    fontSize: '$web-core-fontSize-h-mobile-80 !important',
    margin: '18px 0 34px',
    lineHeight: '$web-core-lineHeight-120 !important',
  },
});

const StepHeading = styled(Heading, {
  '&.abyss-heading-root': {
    fontSize: '$lg',
    lineHeight: '100%',
  },
});

const StyledBadge = styled(Box, {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  marginBottom: '$core-spacing-400',
  [MEDIA_QUERY.mobileAndTablet]: {
    margin: '0 $md 0 0',
    minWidth: '72px',
  },
});

const TIERS = [
  {
    title: 'Digital',
    color: '$accent2',
    images: [
      'daily_burn.svg',
      'volt.svg',
      'fitness_on_demand.svg',
      'yoga_works.svg',
      'fitbit_pre.svg',
    ],
    pricing: '$10',
    saving: '20%',
  },
  {
    title: 'Classic',
    color: '$accent7',
    images: [
      'anytime_fit.svg',
      'planet_fit.svg',
      'eos_fitness.svg',
      'la_fitness.svg',
      'snap_fit.svg',
    ],
    showAdditionalInfo: true,
    pricing: '$34',
    saving: '20%',
  },
  {
    title: 'Standard',
    color: '$accent5',
    images: ['cyclebar.svg', 'row_house.svg', 'camp_gladiator.svg'],
    showAdditionalInfo: true,
    pricing: '$69',
    saving: '27%',
  },
  {
    title: 'Premium',
    color: '$accent3',
    images: [
      'crunch.svg',
      'barre3.svg',
      'rumble.svg',
      'pure_barre.svg',
      'yogasix.svg',
    ],
    showAdditionalInfo: true,
    pricing: '$109',
    saving: '40%',
  },
  {
    title: 'Elite',
    color: '$accent6',
    images: [
      'orangetheory.svg',
      'f45.svg',
      'slabh.svg',
      '9r.svg',
      'title.svg',
      'club_pilates.svg',
    ],
    showAdditionalInfo: true,
    pricing: '$249',
    saving: '36%',
  },
];

export function MembershipOverview() {
  const { t } = useIntl();
  const router = useRouter();
  const { isDesktop } = useStandaloneBreakpoints();

  const STEPS: Array<{
    title: string;
    description: string;
    color: string;
  }> = [
    {
      title: t('membershipOverview.step.one.title'),
      description: t('membershipOverview.step.one.description'),
      color: '$tint1',
    },
    {
      title: t('membershipOverview.step.two.title'),
      description: t('membershipOverview.step.two.description'),
      color: '$tint8',
    },
    {
      title: t('membershipOverview.step.three.title'),
      description: t('membershipOverview.step.three.description'),
      color: '$accent2',
    },
  ];

  const priceText = t('membershipOverview.additionalTierInfo').split(' ')[0];
  const additionalInfoText = t('membershipOverview.additionalTierInfo')
    .split(' ')
    .slice(1)
    .join(' ');

  const handleClick = () => {
    router?.push(getRegistrationUrl(router?.locale));
  };

  return (
    <ContentWidthWrapper css={CONTENT_WRAPPER_STYLES}>
      <Box color="$tint7" padding="0" css={CUSTOM_BOX_STYLES}>
        <Flex direction="column" justify="center" alignItems="center">
          <StyledHeading offset={2} textAlign="center">
            {t('membershipOverview.heading')}
          </StyledHeading>
          <Container direction="column">
            <Flex
              direction={isDesktop ? 'row' : 'column'}
              justify="space-between"
              css={STEP_CONTAINER_STYLES}
            >
              {STEPS.map((step, index) => (
                <Step
                  key={index}
                  direction={isDesktop ? 'column' : 'row'}
                  alignItems="center"
                  justify="flex-start"
                >
                  <StyledBadge height={72} width={72} color={step.color}>
                    <Text color="$primary1" size="24px">
                      {index + 1}
                    </Text>
                  </StyledBadge>
                  <Flex
                    direction="column"
                    alignItems={isDesktop ? 'center' : 'flex-start'}
                    justify="center"
                  >
                    <StepHeading offset={5}>{step.title}</StepHeading>
                    <Text color="$primary1" css={STEP_DESCRIPTION_TEXT_STYLES}>
                      {step.description}
                    </Text>
                  </Flex>
                </Step>
              ))}
            </Flex>
            <Flex
              direction={isDesktop ? 'row' : 'column'}
              justify="space-between"
              alignItems="flex-end"
              css={TIER_CARD_CONTAINER_STYLES}
            >
              {TIERS.map((tier, index) => (
                <Card key={index} css={CUSTOM_CARD_STYLES} size="small">
                  <Card.Section css={CARD_HEADER_SECTION_STYLES}>
                    <Box
                      color={tier.color}
                      padding="$core-spacing-25"
                      align="center"
                    >
                      <Text
                        size="$xl"
                        fontWeight="$bold"
                        color="$primary1"
                        css={TIER_CARD_HEADER_TEXT_STYLES}
                      >
                        {tier.title}
                      </Text>
                    </Box>
                  </Card.Section>
                  <Card.Section css={CUSTOM_CARD_SECTION_STYLES}>
                    <Flex
                      direction="column"
                      justify="space-between"
                      alignItems="center"
                      css={{
                        'abyss-flex-root': {
                          height: isDesktop ? `${190 + index * 32}px` : 'auto',
                          // setting width to match with figma designs
                          width: isDesktop
                            ? 'auto'
                            : tier.showAdditionalInfo
                              ? '139px'
                              : '82px',
                          gap: isDesktop ? '0' : '$sm',
                        },
                      }}
                    >
                      {tier.images.map((imageUrl, idx) => (
                        <img
                          key={idx}
                          src={'images/partners/' + imageUrl}
                          alt={tier.title}
                        />
                      ))}
                      {tier.showAdditionalInfo && (
                        <Text
                          size={isDesktop ? '10px' : '$xs'}
                          color="$gray7"
                          fontWeight="$normal"
                          css={ADDITIONAL_TIER_INFO_STYLES}
                        >
                          <Text
                            size={isDesktop ? '10px' : '$xs'}
                            fontWeight="$bold"
                            color="$accent8"
                          >
                            {`${priceText} `}
                          </Text>
                          {additionalInfoText}
                        </Text>
                      )}
                    </Flex>
                    <Flex
                      direction="column"
                      alignItems="center"
                      justify="center"
                      css={PRICING_INFO_CONTAINER_STYLES}
                    >
                      <Flex alignItems="flex-end">
                        <Heading display={isDesktop ? 2 : 'h1'} color="$gray7">
                          {tier.pricing}
                        </Heading>
                        <Text
                          size={isDesktop ? '$2xs' : '$xs'}
                          fontWeight="$bold"
                          color="$gray7"
                          css={PER_MONTH_TEXT_STYLES}
                        >
                          {'  / mo'}
                        </Text>
                      </Flex>
                      <Text
                        size={isDesktop ? '$2xs' : '$sm'}
                        fontWeight="$normal"
                        css={SAVING_INFO_TEXT_STYLES}
                        color="$gray7"
                      >
                        {t('membershipOverview.savingInfo', {
                          percentage: tier.saving,
                        })}
                      </Text>
                    </Flex>
                  </Card.Section>
                </Card>
              ))}
            </Flex>
          </Container>
          <Flex
            direction="column"
            alignItems="center"
            justify="center"
            css={FIND_YOUR_FIT_CONTAINER_STYLES}
          >
            <Button onClick={handleClick} size="$lg">
              {t('membershipOverview.findYourFitButton')}
            </Button>

            <Text color="$primary1" size="$sm" css={PRICING_INFO_TEXT_STYLES}>
              {t('membershipOverview.pricingInfo')}
            </Text>
          </Flex>
        </Flex>
      </Box>
    </ContentWidthWrapper>
  );
}
